import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PhoneInputWithSearch from '../components/PhoneInputWithSearch';
import { ChevronLeft, Search } from 'lucide-react';

const nationalities = [
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'IE', name: 'Ireland' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'KE', name: 'Kenya' },
    { code: 'TZ', name: 'Tanzania' },
    { code: 'IN', name: 'India' },
    { code: 'JP', name: 'Japan' },
    { code: 'SG', name: 'Singapore' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'BR', name: 'Brazil' },
    { code: 'OTHER', name: 'Other' }
];

export default function Auth() {
    const [searchParams] = useSearchParams();
    const startOnSignUp = searchParams.get('mode') === 'signup';

    const [isSignUp, setIsSignUp] = useState(startOnSignUp);
    const [loading, setLoading] = useState(false);

    // Login form
    const [loginPhone, setLoginPhone] = useState('');
    const [loginPin, setLoginPin] = useState('');

    // Signup form
    const [signupPhone, setSignupPhone] = useState('');
    const [signupEmail, setSignupEmail] = useState('');
    const [signupNationality, setSignupNationality] = useState('');
    const [signupPin, setSignupPin] = useState('');
    const [nationalitySearch, setNationalitySearch] = useState('');

    const { login, register } = useAuth();
    const navigate = useNavigate();

    const filteredNationalities = nationalities.filter(n =>
        n.name.toLowerCase().includes(nationalitySearch.toLowerCase())
    );

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(loginPhone, loginPin);
            toast.success('Welcome back!');
            if (user.role === 'admin') navigate('/admin');
            else if (user.role === 'operator') navigate('/operator');
            else navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(signupPhone, signupEmail.split('@')[0] || 'Traveler', signupPin);
            toast.success('Welcome to safarinodes!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white flex">
            {/* Left - Image */}
            <div
                className="hidden lg:block lg:w-1/2 relative"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1200)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="absolute inset-0 bg-[#073763]/70 flex items-end p-12">
                    <div>
                        <p className="text-white/60 text-sm font-medium mb-2 uppercase tracking-wide">KARIBU</p>
                        <h2 className="text-4xl text-white font-bold mb-4">
                            {isSignUp ? 'Join the Adventure' : 'Welcome Back'}
                        </h2>
                        <p className="text-white/80 max-w-md">
                            {isSignUp
                                ? 'Create your account and start planning your dream Tanzania trip.'
                                : 'Sign in to continue your journey or create an account to start exploring Tanzania.'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Right - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#073763] mb-8 transition-colors" data-testid="auth-back-home">
                        <ChevronLeft size={18} />
                        Back to Home
                    </Link>

                    <h1 className="text-2xl font-bold text-[#073763] mb-8">safarinodes</h1>

                    {!isSignUp ? (
                        /* ===== SIGN IN ===== */
                        <div>
                            <h2 className="text-xl font-semibold text-[#1A1A1A] mb-6" data-testid="auth-title">Sign in</h2>

                            <form onSubmit={handleLogin} className="space-y-5">
                                <div>
                                    <Label className="form-label">Phone Number</Label>
                                    <PhoneInputWithSearch
                                        value={loginPhone}
                                        onChange={setLoginPhone}
                                        defaultCountry="TZ"
                                        data-testid="auth-login-phone"
                                    />
                                </div>

                                <div>
                                    <Label className="form-label">PIN</Label>
                                    <Input
                                        type="password"
                                        placeholder="Enter your PIN"
                                        value={loginPin}
                                        onChange={(e) => setLoginPin(e.target.value)}
                                        className="input-karibu"
                                        maxLength={6}
                                        required
                                        data-testid="auth-login-pin"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="btn-primary w-full py-6 text-base"
                                    disabled={loading}
                                    data-testid="auth-login-submit"
                                >
                                    {loading ? 'Signing in...' : 'Sign In'}
                                </Button>
                            </form>

                            {/* Divider */}
                            <div className="auth-divider my-6">
                                <span>New to safarinodes?</span>
                            </div>

                            {/* Registration options */}
                            <Button
                                type="button"
                                onClick={() => setIsSignUp(true)}
                                className="w-full py-6 text-base bg-white border-2 border-[#073763] text-[#073763] hover:bg-[#073763] hover:text-white rounded-full transition-all"
                                data-testid="auth-switch-to-signup"
                            >
                                Create a Traveler Account
                            </Button>

                            <p className="text-center text-sm text-gray-500 mt-4">
                                <Link to="/operator/register" className="text-[#073763] hover:underline font-medium">
                                    Register as a Tour Operator
                                </Link>
                            </p>
                        </div>
                    ) : (
                        /* ===== SIGN UP (Traveler) ===== */
                        <div>
                            <h2 className="text-xl font-semibold text-[#1A1A1A] mb-1" data-testid="auth-title">Create Account</h2>
                            <p className="text-sm text-gray-500 mb-6">Join thousands of happy travelers</p>

                            <form onSubmit={handleSignup} className="space-y-4">
                                <div>
                                    <Label className="form-label">Phone Number</Label>
                                    <PhoneInputWithSearch
                                        value={signupPhone}
                                        onChange={setSignupPhone}
                                        defaultCountry="US"
                                        data-testid="auth-signup-phone"
                                    />
                                </div>

                                <div>
                                    <Label className="form-label">Email Address</Label>
                                    <Input
                                        type="email"
                                        placeholder="your@email.com"
                                        value={signupEmail}
                                        onChange={(e) => setSignupEmail(e.target.value)}
                                        className="input-karibu"
                                        required
                                        data-testid="auth-signup-email"
                                    />
                                </div>

                                <div>
                                    <Label className="form-label">Nationality</Label>
                                    <Select value={signupNationality} onValueChange={setSignupNationality}>
                                        <SelectTrigger className="input-karibu" data-testid="auth-signup-nationality">
                                            <SelectValue placeholder="Select your nationality" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[300px]">
                                            <div className="px-3 py-2 sticky top-0 bg-white border-b">
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                                    <input
                                                        type="text"
                                                        placeholder="Search countries..."
                                                        value={nationalitySearch}
                                                        onChange={(e) => setNationalitySearch(e.target.value)}
                                                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#073763]"
                                                    />
                                                </div>
                                            </div>
                                            {filteredNationalities.map((nat) => (
                                                <SelectItem key={nat.code} value={nat.code}>
                                                    {nat.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label className="form-label">Create PIN (4-6 digits)</Label>
                                    <Input
                                        type="password"
                                        placeholder="Create a secure PIN"
                                        value={signupPin}
                                        onChange={(e) => setSignupPin(e.target.value)}
                                        className="input-karibu"
                                        maxLength={6}
                                        required
                                        data-testid="auth-signup-pin"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">You'll use this PIN to sign in</p>
                                </div>

                                <Button
                                    type="submit"
                                    className="btn-primary w-full py-6 text-base"
                                    disabled={loading || !signupPhone || !signupEmail || !signupNationality || !signupPin}
                                    data-testid="auth-signup-submit"
                                >
                                    {loading ? 'Creating account...' : 'Create Account'}
                                </Button>
                            </form>

                            {/* Back to Sign In */}
                            <p className="text-center text-sm text-gray-600 mt-5">
                                Already have an account?{' '}
                                <button
                                    type="button"
                                    onClick={() => setIsSignUp(false)}
                                    className="text-[#073763] hover:underline font-medium"
                                    data-testid="auth-switch-to-signin"
                                >
                                    Sign in
                                </button>
                            </p>

                            <p className="text-center text-sm text-gray-500 mt-3">
                                <Link to="/operator/register" className="text-[#073763] hover:underline font-medium">
                                    Register as a Tour Operator instead
                                </Link>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
